/**
 * Version guard — mounts once at app root. On every boot:
 *  1. Fetches /api/version
 *  2. Compares to the version we last saw (localStorage)
 *  3. If mismatch → unregisters SW, purges caches, hard-reloads
 *
 * Also listens for SW_UPDATED messages from the service worker (fires on
 * activate) and reloads the page so users pick up the new bundle without
 * having to hit Ctrl+Shift+R.
 */
import { useEffect } from "react";

const STORAGE_KEY = "mlima_app_version";

async function purgeAndReload() {
    // Best-effort: unregister every SW + delete every cache.
    try {
        if ("serviceWorker" in navigator) {
            const regs = await navigator.serviceWorker.getRegistrations();
            await Promise.all(regs.map((r) => r.unregister()));
        }
    } catch { /* ignore */ }
    try {
        if ("caches" in window) {
            const keys = await caches.keys();
            await Promise.all(keys.map((k) => caches.delete(k)));
        }
    } catch { /* ignore */ }
    // Hard reload — bypasses HTTP cache too.
    window.location.reload();
}

export default function useVersionGuard() {
    useEffect(() => {
        // Listen for SW postMessage after a new SW activates.
        // Guarded by sessionStorage so we reload at most ONCE per session per
        // SW version — otherwise every SW activate → postMessage → reload →
        // fresh page + fresh SW activate causes an infinite full-page reload
        // loop (users see the app flash white every 5–15 s).
        const onSwMessage = (e) => {
            if (e?.data?.type !== "SW_UPDATED") return;
            const swVer = e.data.version || "unknown";
            const flagKey = `md-sw-reloaded-${swVer}`;
            if (sessionStorage.getItem(flagKey)) return;
            sessionStorage.setItem(flagKey, "1");
            if (process.env.NODE_ENV !== "production") console.debug("SW updated to", swVer);
            window.location.reload();
        };
        if ("serviceWorker" in navigator) {
            navigator.serviceWorker.addEventListener("message", onSwMessage);
        }

        // Version probe.
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/version`, {
                    cache: "no-store",
                    credentials: "omit",
                });
                if (!res.ok) return;
                const data = await res.json();
                const remote = data.version;
                if (!remote || cancelled) return;

                const stored = localStorage.getItem(STORAGE_KEY);
                if (stored && stored !== remote) {
                    // Guarded by sessionStorage so we reload at most ONCE per
                    // version per session — mirrors the SW-update guard above.
                    // Without this, any race that re-serves the same stale
                    // bundle right after `purgeAndReload()` (e.g. a lagging
                    // CDN edge) would re-trigger this exact mismatch on the
                    // very next mount and reload forever.
                    const flagKey = `md-version-reloaded-${remote}`;
                    if (sessionStorage.getItem(flagKey)) {
                        localStorage.setItem(STORAGE_KEY, remote);
                        return;
                    }
                    sessionStorage.setItem(flagKey, "1");
                    if (process.env.NODE_ENV !== "production") console.debug("app version changed", stored, "→", remote);
                    localStorage.setItem(STORAGE_KEY, remote);
                    await purgeAndReload();
                    return;
                }
                // First-time visitors: record without reloading.
                if (!stored) localStorage.setItem(STORAGE_KEY, remote);
            } catch (err) {
                if (process.env.NODE_ENV !== "production") console.debug("version probe failed:", err);
            }
        })();

        return () => {
            cancelled = true;
            if ("serviceWorker" in navigator) {
                navigator.serviceWorker.removeEventListener("message", onSwMessage);
            }
        };
    }, []);
}
